import React from 'react';
import { CheckCircle2Icon, AlertTriangleIcon } from 'lucide-react';
import type { CaseDocument, CustodyCase } from '../../../types/case';
import { DocPreview } from '../../shared/DocPreview';
import { StatusBadge } from '../../shared/StatusBadge';
import { DOC_LABEL } from '../../../utils/caseStatus';
import { fmtDateTime } from '../../../utils/bn';
export interface DocReview {
  decision?: 'ok' | 'fix';
  reasonType: string;
  detail: string;
  checks: boolean[];
}
export const CHECKS = ['মালিকের নাম PCR-এর তথ্যের সাথে মিলেছে', 'রেজিস্ট্রেশন / চেসিস নম্বর মিলেছে', 'কাগজের মেয়াদ বৈধ', 'স্ক্যান / ছবি স্পষ্ট ও পাঠযোগ্য', 'মূল কপির সাথে মিলানো হয়েছে (অফিসে জমার ক্ষেত্রে)'];
const REQUIRED_CHECKS = 4;
export const FIX_REASONS = ['ছবি অস্পষ্ট / পাঠযোগ্য নয়', 'কাগজের মেয়াদ উত্তীর্ণ', 'তথ্যে অমিল (নাম / নম্বর)', 'আংশিক কপি — সকল পৃষ্ঠা নেই', 'অন্যান্য'];
export function emptyReview(d: CaseDocument): DocReview {
  return {
    decision: d.status === 'verified' ? 'ok' : undefined,
    reasonType: FIX_REASONS[0],
    detail: '',
    checks: CHECKS.map(() => d.status === 'verified')
  };
}
export const fixText = (r: DocReview) => r.detail.trim() ? `${r.reasonType}: ${r.detail.trim()}` : r.reasonType;
interface Props {
  c: CustodyCase;
  doc: CaseDocument;
  review: DocReview;
  onChange: (r: DocReview) => void;
  readOnly: boolean;
}
export function DocReviewPanel({
  c,
  doc,
  review,
  onChange,
  readOnly
}: Props) {
  const checksOk = review.checks.slice(0, REQUIRED_CHECKS).every(Boolean);
  const toggle = (i: number) => onChange({
    ...review,
    checks: review.checks.map((v, k) => k === i ? !v : v),
    decision: review.decision === 'ok' && i < REQUIRED_CHECKS ? undefined : review.decision
  });
  return <div data-id="element-359" data-mp="5zpdhss1m4x5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2" data-id="element-360" data-mp="5zfpubuh09um">
        <div data-id="element-361" data-mp="1vdax1gjr3acm">
          <h3 className="text-base font-bold text-ink" data-id="element-362" data-mp="1vevtjbpml3d4">{doc.name}</h3>
          <p className="text-xs text-ink-muted" data-id="element-363" data-mp="20d74jprnpjwx">
            {doc.source === 'office' ? 'সরাসরি অফিসে জমা' : doc.source === 'online' ? 'অনলাইনে জমা' : 'জমা হয়নি'} · {fmtDateTime(doc.uploadedAt)}
          </p>
        </div>
        <StatusBadge {...DOC_LABEL[doc.status]} data-id="element-364" data-mp="1tvm3yvxgv9l7" />
      </div>
      <DocPreview large name={doc.name} ownerName={c.ownerName} regNo={c.regNo} fileName={doc.fileName} fileType={doc.fileType} data-id="element-365" data-mp="1ox2foovehlpo" />
      {doc.reason && <p className="mt-2 rounded-lg bg-danger-50 px-3 py-2 text-xs text-danger-700" data-id="element-366" data-mp="1jzj09lmyv2w5">পূর্ববর্তী সংশোধন কারণ: {doc.reason}</p>}

      {doc.fileName && !readOnly && <div className="mt-4 grid gap-4 lg:grid-cols-2" data-id="element-367" data-mp="2cmdazqtzenwf">
          <fieldset data-id="element-368" data-mp="2cozqqdr109pq">
            <legend className="mb-1.5 text-sm font-bold text-ink" data-id="element-369" data-mp="27w6mdqzgz3e6">যাচাই চেকলিস্ট</legend>
            <ul className="space-y-1.5" data-id="element-370" data-mp="27ttx0srdtqr3">
              {CHECKS.map((label, i) => <li key={label} data-id="element-371" data-mp="22v0rpnm91620" data-mpi={i}>
                  <label className="flex cursor-pointer items-start gap-2 text-sm text-ink" data-id="element-372" data-mp="30c7o89i7qeh7">
                    <input type="checkbox" className="mt-1 h-4 w-4 accent-bdgreen" checked={review.checks[i]} onChange={() => toggle(i)} data-id="element-373" data-mp="2vm0xo4454jwb" />
                    <span data-id="element-374" data-mp="33vq78053tz29">{label}{i >= REQUIRED_CHECKS && <span className="text-ink-soft" data-id="element-375" data-mp="33wrju91rqtm3"> (ঐচ্ছিক)</span>}</span>
                  </label>
                </li>)}
            </ul>
          </fieldset>
          <div data-id="element-376" data-mp="3ctif4j8hy4fm">
            <p className="mb-1.5 text-sm font-bold text-ink" data-id="element-377" data-mp="3cx69fklrmuve">এই কাগজের সিদ্ধান্ত</p>
            <div className="grid grid-cols-2 gap-2" data-id="element-378" data-mp="3hvr5r8xwcuek">
              <button type="button" disabled={!checksOk} title={checksOk ? undefined : 'প্রথম চারটি চেকলিস্ট আইটেম নিশ্চিত করুন'} onClick={() => onChange({
            ...review,
            decision: 'ok'
          })} className={`btn ${review.decision === 'ok' ? 'btn-green' : 'btn-outline'}`} data-id="element-379" data-mp="3mtst462cvfvu">
                <CheckCircle2Icon className="h-4 w-4" data-id="element-380" data-mp="4ft9j6ahes8c" /> গ্রহণযোগ্য
              </button>
              <button type="button" onClick={() => onChange({
            ...review,
            decision: 'fix'
          })} className={`btn ${review.decision === 'fix' ? 'btn-danger' : 'btn-outline'}`} data-id="element-381" data-mp="9emol35f2ram">
                <AlertTriangleIcon className="h-4 w-4" data-id="element-382" data-mp="1b4uou5u338sk" /> সংশোধন প্রয়োজন
              </button>
            </div>
            {!checksOk && review.decision !== 'fix' && <p className="mt-1.5 text-xs text-ink-muted" data-id="element-383" data-mp="164yku6ldzhd0">গ্রহণযোগ্য চিহ্নিত করতে প্রথম চারটি চেকলিস্ট আইটেম নিশ্চিত করুন।</p>}
            {review.decision === 'fix' && <div className="mt-3 space-y-2" data-id="element-384" data-mp="1fvb29pxwmuex">
                <select className="input h-10" aria-label="সংশোধনের কারণ" value={review.reasonType} onChange={e => onChange({
            ...review,
            reasonType: e.target.value
          })} data-id="element-385" data-mp="1fwcn2m4q2v9u">
                  {FIX_REASONS.map(r => <option key={r} data-id="element-386" data-mp="n8qawphfiibd">{r}</option>)}
                </select>
                <textarea rows={2} className="textarea" placeholder="মালিকের জন্য বাংলায় নির্দেশনা লিখুন" value={review.detail} onChange={e => onChange({
            ...review,
            detail: e.target.value
          })} data-id="element-387" data-mp="117q32745hfca" />
              </div>}
          </div>
        </div>}
      {!doc.fileName && <p className="mt-3 text-sm text-ink-muted" data-id="element-388" data-mp="28l05m9y376jp">এই কাগজটি এখনো জমা হয়নি। মালিকের নিকট চাইতে “অতিরিক্ত কাগজপত্র চাই” ব্যবহার করুন অথবা অফিসে জমা লিপিবদ্ধ করুন।</p>}
    </div>;
}